import QRCode from 'qrcode';

interface BrandedQrOptions {
  width?: number;
  logoUrl?: string;
  logoScale?: number;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Unable to load the MTIB logo for the QR code.'));
    image.src = src;
  });
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}

export async function generateBrandedQrDataUrl(
  value: string,
  options: BrandedQrOptions = {},
): Promise<string> {
  const width = options.width ?? 1000;
  const logoScale = options.logoScale ?? 0.22;
  const logoUrl = options.logoUrl ?? '/mtib-logo.png';
  const canvas = document.createElement('canvas');

  await QRCode.toCanvas(canvas, value, {
    width,
    margin: 4,
    errorCorrectionLevel: 'H',
    color: { dark: '#000000', light: '#ffffff' },
  });

  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas.toDataURL('image/png');

  try {
    const logo = await loadImage(logoUrl);
    const maxLogoDimension = Math.round(width * logoScale);
    const sourceWidth = logo.naturalWidth || logo.width;
    const sourceHeight = logo.naturalHeight || logo.height;
    if (!sourceWidth || !sourceHeight) throw new Error('Unable to determine the MTIB logo dimensions for the QR code.');
    const aspectRatio = sourceWidth / sourceHeight;
    const logoWidth = aspectRatio >= 1
      ? maxLogoDimension
      : Math.max(1, Math.round(maxLogoDimension * aspectRatio));
    const logoHeight = aspectRatio <= 1
      ? maxLogoDimension
      : Math.max(1, Math.round(maxLogoDimension / aspectRatio));
    const badgePadding = Math.round(width * 0.018);
    const badgeWidth = logoWidth + badgePadding * 2;
    const badgeHeight = logoHeight + badgePadding * 2;
    const badgeX = Math.round((width - badgeWidth) / 2);
    const badgeY = Math.round((width - badgeHeight) / 2);
    const radius = Math.round(width * 0.018);

    ctx.save();
    roundedRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, radius);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    const logoX = Math.round((width - logoWidth) / 2);
    const logoY = Math.round((width - logoHeight) / 2);
    ctx.drawImage(logo, logoX, logoY, logoWidth, logoHeight);
    ctx.restore();
  } catch {
    // If branding fails to load, keep a fully scannable unbranded QR instead.
  }

  return canvas.toDataURL('image/png');
}
